import { vulnerabilityStatusDescriptionToValue } from '../../shared'
import {
  GQLVulnerability,
  GQLVulnerabilityApplication,
  VulnerabilitySeverity
} from '../../types/vulnerability'
import { transformRoleRequired } from './role'
import { normalizeBugTrackers } from './bug-trackers'
import { VulnerabilityPolicy } from '../../types/vulnerability-policy'
import { Connector } from '../../../../api/connector'

function normalizeApplication({
  app_id: id,
  name,
  context_path: url,
  importance_description: importance,
  license_level,
  language,
  roles
}): GQLVulnerabilityApplication {
  return {
    id,
    name,
    url,
    importance,
    licensed: license_level === 'Licensed',
    language,
    role: transformRoleRequired(roles)
  }
}

export function normalizeSeverity(severity: string): VulnerabilitySeverity {
  const severityMap = {
    Note: 'NOTE',
    Low: 'LOW',
    Medium: 'MEDIUM',
    High: 'HIGH',
    Critical: 'CRITICAL'
  }
  return severityMap[severity]
}

export function normalizeSessionMetadata(metadataResponse) {
  if (Array.isArray(metadataResponse)) {
    return metadataResponse.map(({ session_id, metadata }) => ({
      sessionID: session_id,
      metadata: Array.isArray(metadata)
        ? metadata.map(({ agent_label, display_label, value }) => ({
            agentLabel: agent_label,
            displayLabel: display_label,
            value
          }))
        : []
    }))
  }
  return []
}

export function normalizeVulnerability({
  uuid: id,
  title,
  rule_title: ruleName,
  visible,
  application,
  parent_application,
  severity,
  default_severity: defaultSeverity,
  tags,
  first_time_seen: firstDetected,
  last_time_seen: lastDetected,
  status,
  sub_status,
  reported_to_bug_tracker,
  reported_to_bug_tracker_time,
  bugtracker_tickets,
  violations,
  pending_status,
  pending_status_creation_time,
  session_metadata,
  protectStatus
}): GQLVulnerability {
  return {
    id,
    title,
    ruleName,
    visible,
    severity: normalizeSeverity(severity),
    defaultSeverity,
    tags,
    application: parent_application
      ? normalizeApplication(parent_application)
      : normalizeApplication(application),
    firstDetected,
    lastDetected,
    status: vulnerabilityStatusDescriptionToValue[status],
    substatus: sub_status === '' ? null : sub_status,
    reportedToBugTracker: reported_to_bug_tracker,
    reportedToBugTrackerTime: reported_to_bug_tracker_time || null,
    bugTrackers: normalizeBugTrackers(bugtracker_tickets),
    violations: Array.isArray(violations)
      ? violations.map(({ policy_id: policyId, name }) => ({
          id: policyId,
          name
        }))
      : [],
    pendingStatus: pending_status_creation_time
      ? {
          status: pending_status,
          created: pending_status_creation_time
        }
      : null,
    sessions: normalizeSessionMetadata(session_metadata),
    protectStatus
  }
}

export const normalizeVulnerabilityPolicy = ({
  policy_id: id,
  name,
  enabled,
  rules = [],
  all_rules: allRules,
  applications = [],
  all_applications: allApplications,
  server_environments: serverEnvironments = [],
  all_server_environments: allServerEnvironments,
  application_importance: applicationImportance = [],
  rule_severities: ruleSeverities = [],
  remediation_days: remediationDays,
  valid,
  action,
  time_based_enabled: timeBasedEnabled,
  route_based_enabled: routeBasedEnabled,
  auto_verification_type: autoVerificationType
}): VulnerabilityPolicy => ({
  id,
  name,
  enabled,
  rules: rules.map(({ name, title }) => ({ name, title })),
  allRules,
  applications: applications.map(({ app_id: id, name }) => ({ id, name })),
  allApplications,
  serverEnvironments: serverEnvironments.map(({ value }) => value),
  allServerEnvironments,
  applicationImportance,
  ruleSeverities,
  remediationDays,
  valid,
  action,
  timeBasedEnabled,
  routeBasedEnabled,
  autoVerificationType
})

export const expandParams = 'application,bugtracker,violations'

export async function findByIDAndOrganizationID(
  vulnerabilityID: string,
  organizationID: string,
  connector: Connector
): Promise<GQLVulnerability> {
  const { trace } = await connector.get(
    `/${organizationID}/orgtraces/filter/${vulnerabilityID}?expand=${expandParams}`
  )
  return normalizeVulnerability(trace)
}
